import { TFunction, useTranslation } from "next-i18next";

import { InvoiceType, OrderFields, OrderStep } from "../../constants";
import getDeliveryMethods from "../../data/getDeliveryMethods";
import type { TOrderInitialValuesData, TStaticInitialOrderData } from "../../types";

const getCommonInfoInitialValues = (currentStep: OrderStep, weightRegulations: string[] | null) => ({
  [OrderFields.IS_DOMESTIC]: true,
  [OrderFields.IS_INTRA_EUROPE]: false,
  [OrderFields.IS_FROM_ORIGIN]: true,
  [OrderFields.CURRENT_STEP]: currentStep,
  [OrderFields.PROMO_CODE]: "",
  [OrderFields.OFFER_GROUP_ID]: "",
  [OrderFields.SHIPMENT_ID]: null,
  [OrderFields.SHIPMENT_SIGNED_IN]: false,
  [OrderFields.AFFILIATE_ID]: null,
  [OrderFields.AFFILIATE_RETURN_URL]: null,
  [OrderFields.WEIGHT_REGULATIONS]: weightRegulations,
});

const getPackageInfoInitialValues = (t: TFunction, staticOrderData?: TStaticInitialOrderData) => {
  const deliveryMethods = getDeliveryMethods(t);

  if (!staticOrderData) {
    return {
      [OrderFields.STARTING_COUNTRY]: null,
      [OrderFields.STARTING_COUNTRY_NAME]: "",
      [OrderFields.DESTINATION_COUNTRY]: null,
      [OrderFields.DESTINATION_COUNTRY_NAME]: "",
      [OrderFields.PACKAGE_SIZE]: null,
      [OrderFields.DELIVERY_METHOD]: deliveryMethods[0].value,
      [OrderFields.PACKAGE_WEIGHT]: null,
    };
  }

  const { countriesFrom, countriesTo, packageSizes } = staticOrderData;

  const defaultCountryFrom = countriesFrom.find(({ isDefault }) => !!isDefault)!;
  const defaultCountryTo = countriesTo.find(({ isDefault }) => !!isDefault)!;

  return {
    [OrderFields.STARTING_COUNTRY]: defaultCountryFrom?.code || null,
    [OrderFields.STARTING_COUNTRY_NAME]: defaultCountryFrom?.title || "",
    [OrderFields.DESTINATION_COUNTRY]: defaultCountryTo?.code || null,
    [OrderFields.DESTINATION_COUNTRY_NAME]: defaultCountryTo?.title || "",
    [OrderFields.PACKAGE_SIZE]: packageSizes?.[0]?.title || null,
    [OrderFields.DELIVERY_METHOD]: deliveryMethods[0].value,
    [OrderFields.PACKAGE_WEIGHT]: null,
  };
};

const getShipmentInitialValues = () => ({
  [OrderFields.SENDER_NAME]: "",
  [OrderFields.SENDER_PHONE_NUMBER]: "",
  [OrderFields.SENDER_EMAIL]: "",
  [OrderFields.RETURN_LOCKER]: null,
  [OrderFields.RETURN_LOCKER_NAME]: "",
  [OrderFields.RECEIVER_NAME]: "",
  [OrderFields.RECEIVER_PHONE_NUMBER]: "",
  [OrderFields.RECEIVER_EMAIL]: "",
  [OrderFields.RECEIVER_LOCKER]: null,
  [OrderFields.RECEIVER_LOCKER_NAME]: "",
  [OrderFields.RECEIVER_STREET]: "",
  [OrderFields.RECEIVER_HOUSE_NO]: "",
  [OrderFields.RECEIVER_FLAT_NO]: "",
  [OrderFields.RECEIVER_CITY]: "",
  [OrderFields.RECEIVER_ZIP_CODE]: "",
  [OrderFields.RECEIVER_COMMENTS]: "",
  [OrderFields.RECEIVER_ADDRESS_ID]: null,
});

const getSenderInitialValues = () => ({
  [OrderFields.SENDER_STREET]: "",
  [OrderFields.SENDER_HOUSE_NO]: "",
  [OrderFields.SENDER_FLAT_NO]: "",
  [OrderFields.SENDER_CITY]: "",
  [OrderFields.SENDER_ZIP_CODE]: "",
  [OrderFields.SENDER_COMMENTS]: "",
  [OrderFields.LABEL_PRINT]: null,
  [OrderFields.PICKUP_DATE]: "",
  [OrderFields.PICKUP_TIME_FROM]: "",
  [OrderFields.PICKUP_TIME_TILL]: "",
  [OrderFields.SENDER_AS_CONTACT_PERSON]: true,
  [OrderFields.CONTACT_PERSON_NAME]: "",
  [OrderFields.CONTACT_PERSON_PHONE_NUMBER]: "",
  [OrderFields.CONTACT_PERSON_EMAIL]: "",
});

const getNewAddressInitialValues = () => ({
  [OrderFields.HAS_NEW_ADDRESS]: false,
  [OrderFields.NEW_ADDRESS_NAME]: "",
  [OrderFields.NEW_ADDRESS_PHONE]: "",
  [OrderFields.NEW_ADDRESS_EMAIL]: "",
  [OrderFields.NEW_ADDRESS_LOCKER]: "",
  [OrderFields.NEW_ADDRESS_LOCKER_NAME]: "",
  [OrderFields.NEW_ADDRESS_STREET]: "",
  [OrderFields.NEW_ADDRESS_HOUSE_NO]: "",
  [OrderFields.NEW_ADDRESS_FLAT_NO]: "",
  [OrderFields.NEW_ADDRESS_CITY]: "",
  [OrderFields.NEW_ADDRESS_ZIP_CODE]: "",
});

const getSummaryInitialValues = () => ({
  [OrderFields.INVOICE_TYPE]: InvoiceType.PRIVATE,
  [OrderFields.INVOICE_NAME]: "",
  [OrderFields.INVOICE_EMAIL]: "",
  [OrderFields.INVOICE_COMPANY_NAME]: "",
  [OrderFields.INVOICE_COMPANY_EMAIL]: "",
  [OrderFields.INVOICE_REGISTRY_CODE]: "",
  [OrderFields.INVOICE_VAT_NUMBER]: "",
  [OrderFields.INVOICE_REGISTRATION_ADDRESS]: "",
  [OrderFields.TERMS_AGREEMENT]: false,
  [OrderFields.PAYMENT_METHOD]: "",
  [OrderFields.PRICE]: undefined,
  [OrderFields.ORIGINAL_PRICE]: undefined,
});

const useInitialValues = (
  currentStep: OrderStep,
  weightRegulations: string[] | null,
  staticOrderData?: TStaticInitialOrderData,
): TOrderInitialValuesData => {
  const { t } = useTranslation();

  const commonInfo = getCommonInfoInitialValues(currentStep, weightRegulations);
  const packageInfo = getPackageInfoInitialValues(t, staticOrderData);
  const shipment = getShipmentInitialValues();
  const sender = getSenderInitialValues();
  const newAddress = getNewAddressInitialValues();
  const summary = getSummaryInitialValues();

  const initialValues = { ...commonInfo, ...packageInfo, ...shipment, ...sender, ...newAddress, ...summary };

  return initialValues;
};
export default useInitialValues;
